import axios from 'axios';
import { decryptPayload } from '@/services/crypto';
import { useAuthStore } from '@/store/useAuthStore';
import { useEnvironmentStore } from '@/store/useEnvironmentStore';

/**
 * Talks directly to merchant-api (same host/contract merchant-frontend uses),
 * as opposed to `services/api.js` which talks to developer-api's own backend.
 * Request bodies are sent as plain JSON — merchant-frontend's own login call does the
 * same (see AxiosAuthInstance.js) and merchant-api's decryptRequest middleware only
 * decrypts when a `payload` field is present, so plain JSON is accepted everywhere.
 * Responses are always `{ payload: <encrypted> }` server-side (encryptResponse is
 * unconditional), so decrypting them back is mandatory.
 */
const merchantApiClient = axios.create({
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

function decryptResponseData(data) {
  if (data && typeof data === 'object' && typeof data.payload === 'string') {
    try {
      return decryptPayload(data.payload);
    } catch {
      return data;
    }
  }
  return data;
}

merchantApiClient.interceptors.request.use((config) => {
  if (config.url && !/^https?:\/\//i.test(config.url)) {
    const env = useEnvironmentStore.getState().activeEnv();
    const baseUrlVar = env?.variables.find((v) => v.key === 'baseUrl' && v.enabled);
    if (baseUrlVar?.value) config.baseURL = baseUrlVar.value;
  }

  config.headers = config.headers || {};
  const { accessToken } = useAuthStore.getState();
  if (accessToken && !config.headers.Authorization && !config.__skipBearer) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // FormData needs the browser to set its own multipart boundary — the instance's
  // default 'application/json' would otherwise silently break file-upload endpoints.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

let isRefreshing = false;
let subscribers = [];

function subscribeTokenRefresh(cb) {
  subscribers.push(cb);
}

function onRefreshed(token) {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
}

merchantApiClient.interceptors.response.use(
  (response) => {
    response.data = decryptResponseData(response.data);
    return response;
  },
  async (error) => {
    const { response, config } = error;
    if (response) response.data = decryptResponseData(response.data);

    const isAuthLifecycleCall = config?.url?.includes('/auth/login') || config?.url?.includes('/auth/refresh-token');

    // On a stale access token, refresh and retry once. If the refresh itself fails
    // (e.g. the refresh token is genuinely invalid), just surface the original error —
    // the session is left intact and the background refresher keeps retrying, rather
    // than forcing a logout/redirect out from under the merchant.
    if (response?.status === 401 && config && !config._retry && !config.__skipBearer && !isAuthLifecycleCall && useAuthStore.getState().refreshToken) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token) => {
            if (!token) return reject(error);
            config._retry = true;
            config.headers.Authorization = `Bearer ${token}`;
            resolve(merchantApiClient(config));
          });
        });
      }

      config._retry = true;
      isRefreshing = true;
      try {
        const newToken = await useAuthStore.getState().refreshTokens();
        isRefreshing = false;
        onRefreshed(newToken);
        config.headers.Authorization = `Bearer ${newToken}`;
        return merchantApiClient(config);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshed(null);
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default merchantApiClient;
